// ISC License
// Copyright (c) for portions of Lucide are held by Cole Bemis 2013-2023 as part of Feather (MIT). All other copyright (c) for Lucide are held by Lucide Contributors 2025.
// Permission to use, copy, modify, and/or distribute this software for any purpose with or without fee is hereby granted, provided that the above copyright notice and this permission notice appear in all copies.
// THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.

import { useState } from 'react';
import { Mail, MapPin, Phone, FileText, Building } from 'lucide-react';

const Contact = () => {
    const [formData, setFormData] = useState({ name: '', email: '', message: '' });
    const [statusMessage, setStatusMessage] = useState(null);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        setStatusMessage({ text: 'Wysyłanie wiadomości...', type: 'info' });
        
        try {
            const response = await fetch('/api/send-email', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            if (response.ok) {
                setStatusMessage({ text: 'Dziękujemy za wiadomość! Odpowiemy najszybciej jak to możliwe.', type: 'success' });
                setFormData({ name: '', email: '', message: '' });
            } else {
                const errorData = await response.json();
                setStatusMessage({ text: `Błąd: ${errorData.message || 'Wystąpił błąd po stronie serwera.'}`, type: 'error' });
            }

        } catch (error) {
            setStatusMessage({ text: 'Błąd połączenia. Spróbuj ponownie później.', type: 'error' });
        }

        setTimeout(() => {
            setStatusMessage(null);
        }, 4000);
    };

    const messageClass = statusMessage?.type === 'success' ? 'bg-green-500' : 'bg-red-500';

    return (
        <section id="contact" className="py-20 px-4 md:px-12 bg-[#1B5125] text-white">
            <div className="max-w-7xl mx-auto">
                <h2 className="text-4xl font-extrabold text-white sm:text-5xl tracking-tight mb-12">
                    Skontaktuj się
                </h2>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                    <div>
                        <h3 className="text-2xl font-semibold mb-6">Wyślij Wiadomość</h3>

                        {statusMessage && (
                            <div className={`mb-4 p-3 rounded-lg ${messageClass} text-white text-center font-medium opacity-100 transition-opacity duration-500`}>
                                {statusMessage.text}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <input
                                type="text"
                                name="name"
                                placeholder="Imię i Nazwisko / Nazwa Firmy"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                className="w-full p-3 rounded-lg bg-gray-200 border border-gray-300 focus:border-green-500 focus:ring-green-500 text-gray-900 placeholder-gray-500 invalid:border-red-500 invalid:text-red-600 focus:invalid:border-red-500 focus:invalid:ring-red-500"
                            />
                            <input
                                type="email"
                                name="email"
                                placeholder="Adres Email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                className="w-full p-3 rounded-lg bg-gray-200 border border-gray-300 focus:border-green-500 focus:ring-green-500 text-gray-900 placeholder-gray-500 invalid:border-red-500 invalid:text-red-600 focus:invalid:border-red-500 focus:invalid:ring-red-500"
                            />
                            <textarea
                                name="message"
                                rows="5"
                                placeholder="Treść wiadomości / Opis sprawy"
                                value={formData.message}
                                onChange={handleChange}
                                required
                                className="w-full p-3 rounded-lg bg-gray-200 border border-gray-300 focus:border-green-500 focus:ring-green-500 text-gray-900 placeholder-gray-500 invalid:border-red-500 invalid:text-red-600 focus:invalid:border-red-500 focus:invalid:ring-red-500"
                            ></textarea>
                            <button
                                type="submit"
                                className="w-full bg-gray-200 hover:bg-gray-300 text-[#1B5125] font-bold py-3 px-6 rounded-lg shadow-lg uppercase tracking-wider transition-colors duration-300 transform hover:scale-[1.01]"
                            >
                                Wyślij
                            </button>
                        </form>
                    </div>

                    <div>
                        <h3 className="text-2xl font-semibold mb-6">Dane Kontaktowe</h3>
                        <div className="space-y-4 text-lg">
                            <div className="flex items-start">
                                <Mail className="text-white mr-4 mt-1 flex-shrink-0" />
                                <div>
                                    <span className="font-bold block">E-mail:</span>
                                    <a href="mailto:kancelaria@fedyszyn-olszewska.pl" className="hover:text-white transition-colors duration-300">kancelaria@fedyszyn-olszewska.pl</a>
                                </div>
                            </div>
                            <div className="flex items-start">
                                <Phone className="text-white mr-4 mt-1 flex-shrink-0" />
                                <div>
                                    <span className="font-bold block">Telefon:</span>
                                    <a href="tel:+48123456789" className="hover:text-white transition-colors duration-300">+48 662 276 973</a>
                                </div>
                            </div>
                            <div className="flex items-start">
                                <MapPin className="text-white mr-4 mt-1 flex-shrink-0" />
                                <div>
                                    <span className="font-bold block">Adres Kancelarii:</span>
                                    <p>al. Kościuszki 93 lok. 14, 90-436 Łódź</p>
                                </div>
                            </div>
                            <div className="flex items-start">
                                <FileText className="text-white mr-4 mt-1 flex-shrink-0" />
                                <div>
                                    <span className="font-bold block">NIP:</span>
                                    <p>7262714626</p>
                                </div>
                            </div>
                            <div className="flex items-start">
                                <Building className="text-white mr-4 mt-1 flex-shrink-0" />
                                <div>
                                    <span className="font-bold block">REGON:</span>
                                    <p>542606220</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Contact;